/**
 * Suporte a texto rico nas observações/detalhes de alertas.
 * O conteúdo é trafegado como HTML simples; o limite de caracteres considera
 * apenas o texto visível (sem tags), para bater com o limite da API.
 */

export const ALERT_OBSERVATION_MAX_LENGTH = 2000;

const ALLOWED_TAGS = new Set([
  "B",
  "STRONG",
  "I",
  "EM",
  "U",
  "S",
  "STRIKE",
  "BR",
  "P",
  "DIV",
  "UL",
  "OL",
  "LI",
  "A",
]);

const SAFE_LINK_PROTOCOLS = ["http:", "https:", "mailto:"];

function isSafeHref(href) {
  try {
    const url = new URL(href, window.location.origin);
    return SAFE_LINK_PROTOCOLS.includes(url.protocol);
  } catch {
    return false;
  }
}

/** Remove tags/atributos fora da allowlist, preservando o texto. */
export function sanitizeRichTextHtml(html) {
  if (!html) return "";

  const template = document.createElement("template");
  template.innerHTML = html;

  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) return;

      if (child.nodeType !== Node.ELEMENT_NODE) {
        child.remove();
        return;
      }

      if (!ALLOWED_TAGS.has(child.tagName)) {
        // Mantém o conteúdo textual, descarta a tag
        const parent = child.parentNode;
        while (child.firstChild) parent.insertBefore(child.firstChild, child);
        child.remove();
        return;
      }

      Array.from(child.attributes).forEach((attr) => {
        const keepHref =
          child.tagName === "A" &&
          attr.name === "href" &&
          isSafeHref(attr.value);

        if (!keepHref) child.removeAttribute(attr.name);
      });

      if (child.tagName === "A") {
        child.setAttribute("target", "_blank");
        child.setAttribute("rel", "noopener noreferrer");
      }

      walk(child);
    });
  };

  walk(template.content);

  return template.innerHTML;
}

/** Desfaz `&lt;p&gt;` quando o banco guarda o HTML escapado. */
function unescapeHtml(value) {
  if (!value || !/&(?:lt|gt|amp|quot|#39);/i.test(value)) return value;
  return value
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/gi, "&");
}

/** Texto visível do HTML — usado para contagem e para pré-visualizações em tabela. */
export function richTextToPlainText(value) {
  if (!value) return "";
  value = unescapeHtml(value);
  if (!/<[a-z][\s\S]*>/i.test(value)) return value;

  const template = document.createElement("template");
  template.innerHTML = value
    .replace(/<\/(p|div|li|ul|ol)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n");

  return (template.content.textContent || "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function richTextLength(value) {
  return richTextToPlainText(value).length;
}

/** Converte texto puro (com quebras de linha) em HTML de parágrafos. */
export function plainTextToRichText(text) {
  if (!text) return "";
  text = unescapeHtml(text);
  if (/<[a-z][\s\S]*>/i.test(text)) return sanitizeRichTextHtml(text);

  return text
    .split(/\n{2,}/)
    .map((block) => `<p>${block.replace(/\n/g, "<br>")}</p>`)
    .join("");
}

export function isRichTextEmpty(value) {
  return richTextToPlainText(value).length === 0;
}
